import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from '../../../core/hooks/useAsync';
import { getHomePageDeliveryList } from '../api/RiderHomeApi';
import {
    HomePageDeliveryCountRequest,
    HomePageDeliveryCountResponse,
    HomePageDeliveryStats,
    DEFAULT_HOME_PAGE_DELIVERY_STATS,
} from '../types/homePage';

export const useHomePageForRider = () => {
    const [deliveryStats, setDeliveryStats] = useState<HomePageDeliveryStats>(DEFAULT_HOME_PAGE_DELIVERY_STATS);
    const [selectedDate, setSelectedDate] = useState<string>('');
    
    const deliveryListState = useAsync(getHomePageDeliveryList);

    const getDateDisplayText = (date: Date, daysAgo: number) => {
        return date.toISOString().split('T')[0];
    };

    const dateOptions = useMemo(() => {
        const dates = [];
        const today = new Date();
        
        for (let i = 0; i <= 2; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            const dateString = date.toISOString().split('T')[0];
            const displayText = getDateDisplayText(date, i);
            dates.push({ value: dateString, label: displayText });
        }
        
        if (selectedDate && !dates.find(d => d.value === selectedDate)) {
            dates.unshift({ value: selectedDate, label: selectedDate });
        }
        
        return dates;
    }, [selectedDate]);

    useEffect(() => {
        if (dateOptions && dateOptions.length > 0 && !selectedDate) {
            setSelectedDate(dateOptions[0].value);
        }
    }, [dateOptions, selectedDate]);

    const selectedYyyymmdd = useMemo(() => selectedDate.replace(/-/g, ''), [selectedDate]);

    const fetchDeliveryData = useCallback(async () => {
        if (!selectedYyyymmdd) return;

        try {
            const requestParams: HomePageDeliveryCountRequest = {
                fromDate: selectedYyyymmdd,
            };

            const responseData = await deliveryListState.execute(requestParams);
            
            const deliveryData = responseData.deliveryDataList?.[0];
            if (deliveryData) {
                const totalCompleted = deliveryData.finishCnt || 0;
                const totalRejected = deliveryData.rejectCnt || 0;
                const totalRequests = totalCompleted + totalRejected + (deliveryData.assingCancelCnt || 0) + (deliveryData.deliCancelCnt || 0);
                const acceptanceRate = totalRequests > 0 ? Math.round((totalCompleted / totalRequests) * 100) : 0;
                
                setDeliveryStats({
                    completedCount: totalCompleted,
                    rejectedCount: totalRejected,
                    acceptanceRate: acceptanceRate,
                    deliveryFee: deliveryData.totDeliveryAtm || 0,
                    morningLunchPeakCount: deliveryData.morTimeCnt || 0,
                    afternoonNonPeakCount: deliveryData.afterNonCnt || 0,
                    eveningPeakCount: deliveryData.nightCnt || 0,
                    nightPeakCount: deliveryData.nightNonCnt || 0,
                });
            } else {
                setDeliveryStats(DEFAULT_HOME_PAGE_DELIVERY_STATS);
            }
        } catch (err) {
            setDeliveryStats(DEFAULT_HOME_PAGE_DELIVERY_STATS);
        }
    }, [selectedYyyymmdd]);

    const handleDateChange = useCallback((date: string) => {
        setSelectedDate(date);
    }, []);

    const refreshData = useCallback(() => {
        fetchDeliveryData();
    }, [fetchDeliveryData]);

    useEffect(() => {
        if (selectedYyyymmdd) {
            fetchDeliveryData();
        }
    }, [selectedYyyymmdd, fetchDeliveryData]);

    return {
        deliveryStats,
        selectedDate,
        dateOptions,
        isLoading: deliveryListState.isLoading,
        error: deliveryListState.error,
        handleDateChange,
        refreshData,
        isDataLoaded: deliveryStats.completedCount > 0 || deliveryStats.rejectedCount > 0,
    };
};
